import { useState } from "react";
import { useAbschlaegeForProjekt } from "../../hooks/useInvoices";
import { euro, shortDate, type AbschlagAbsetzung, type WebInvoiceDraft } from "../../lib/invoices";
import { absetzungenSumme, computeWebDraftTotals } from "../../lib/invoiceEditor";

type Props = {
  draft: WebInvoiceDraft;
  onChange: (draft: WebInvoiceDraft) => void;
};

const leereAbsetzung = (): AbschlagAbsetzung => ({
  nummer: "", datum: null, netto: 0, mwst: 0, brutto: 0,
});

/**
 * Abschläge einer Schlussrechnung absetzen (§ 14 Abs. 5 UStG).
 *
 * Der Desktop zieht die absetzbaren Abschlagsrechnungen über das Projekt am
 * Beleg — genau so hier: ohne Projekt gibt es nichts zu holen, dann bleibt
 * die Zeile von Hand. Die Absetzungen ändern die Positionssummen nicht, sie
 * werden im PDF unter dem Bruttobetrag abgezogen.
 */
export default function InvoiceAbschlaege({ draft, onChange }: Props) {
  const [notice, setNotice] = useState("");
  const abschlaege = useAbschlaegeForProjekt(draft.source_project_id);
  const absetzungen = draft.abschlag_absetzungen || [];
  const totals = computeWebDraftTotals(draft);
  const abgesetzt = absetzungenSumme(absetzungen);

  const replace = (next: AbschlagAbsetzung[]) => onChange({ ...draft, abschlag_absetzungen: next });
  const update = (index: number, patch: Partial<AbschlagAbsetzung>) => {
    const next = [...absetzungen];
    next[index] = { ...next[index], ...patch };
    replace(next);
  };

  const uebernehmen = () => {
    const gefunden = abschlaege.data?.items || [];
    if (!draft.source_project_id) {
      setNotice("Ohne Projekt am Beleg lassen sich keine Abschläge ziehen — Projekt oben zuordnen.");
      return;
    }
    if (!gefunden.length) {
      setNotice("Für dieses Projekt sind keine Abschlagsrechnungen gestellt.");
      return;
    }
    const bekannt = new Set(absetzungen.map((item) => item.nummer.trim().toLowerCase()));
    const neu = gefunden.filter((item) => !bekannt.has(item.nummer.trim().toLowerCase()));
    if (!neu.length) {
      setNotice("Alle Abschläge dieses Projekts sind bereits abgesetzt.");
      return;
    }
    replace([...absetzungen, ...neu]);
    setNotice(`${neu.length} Abschlag${neu.length === 1 ? "" : "srechnungen"} übernommen.`);
  };

  return (
    <section className="invoice-editor-card invoice-abschlag-card">
      <div className="invoice-editor-card-head">
        <div>
          <p className="invoice-kicker">Schlussrechnung</p>
          <h2>Abschläge absetzen</h2>
        </div>
        <button className="invoice-button" type="button" onClick={uebernehmen} disabled={abschlaege.isFetching}>
          {abschlaege.isFetching ? "Sucht …" : "⤓ Aus Projekt übernehmen"}
        </button>
      </div>
      <p className="invoice-editor-hint">
        Bereits gestellte Abschlagsrechnungen samt Steuer müssen hier einzeln stehen — so verlangt es § 14 Abs. 5 UStG.
      </p>
      {notice && <p className="invoice-inline-note" role="status">{notice}</p>}

      {absetzungen.length > 0 && (
        <div className="invoice-abschlag-list">
          {absetzungen.map((absetzung, index) => (
            <div className="invoice-abschlag-row" key={`${absetzung.nummer}-${index}`}>
              <label className="invoice-editor-field compact">
                <span>Nummer</span>
                <input value={absetzung.nummer} onChange={(event) => update(index, { nummer: event.target.value })} placeholder="RE0042" />
              </label>
              <label className="invoice-editor-field compact">
                <span>Datum</span>
                <input type="date" value={absetzung.datum || ""} onChange={(event) => update(index, { datum: event.target.value || null })} />
              </label>
              <label className="invoice-editor-field compact">
                <span>Netto</span>
                <div className="invoice-money-input"><input type="number" step="0.01" value={absetzung.netto} onChange={(event) => update(index, { netto: Number(event.target.value) })} /><b>€</b></div>
              </label>
              <label className="invoice-editor-field compact">
                <span>USt.</span>
                <div className="invoice-money-input"><input type="number" step="0.01" value={absetzung.mwst} onChange={(event) => update(index, { mwst: Number(event.target.value) })} /><b>€</b></div>
              </label>
              <label className="invoice-editor-field compact">
                <span>Brutto</span>
                <div className="invoice-money-input"><input type="number" step="0.01" value={absetzung.brutto} onChange={(event) => update(index, { brutto: Number(event.target.value) })} /><b>€</b></div>
              </label>
              <button
                className="invoice-line-remove"
                type="button"
                onClick={() => replace(absetzungen.filter((_, position) => position !== index))}
                aria-label={`Absetzung ${absetzung.nummer || index + 1} entfernen`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {absetzungen.length === 0 && (
        <p className="invoice-empty-copy">Noch nichts abgesetzt — die Schlussrechnung stellt den vollen Betrag in Rechnung.</p>
      )}

      <footer className="invoice-position-actions">
        <button className="invoice-button" type="button" onClick={() => replace([...absetzungen, leereAbsetzung()])}>+ Absetzung von Hand</button>
        {absetzungen.length > 0 && (
          <span className="invoice-abschlag-sum">
            Abgesetzt {euro(abgesetzt)} · Restbetrag <strong>{euro(Math.round((totals.gross - abgesetzt) * 100) / 100)}</strong>
          </span>
        )}
      </footer>

      {!!abschlaege.data?.items?.length && (
        <p className="invoice-editor-hint">
          Im Projekt gefunden: {abschlaege.data.items.map((item) => `${item.nummer} (${shortDate(item.datum || "")})`).join(" · ")}
        </p>
      )}
    </section>
  );
}
